import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Grid, GridColumn, GridToolbar } from '@progress/kendo-react-grid'
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs'
import { Button } from '@progress/kendo-react-buttons'
import { Form, Field, FormElement } from '@progress/kendo-react-form'
import {
  FaEdit,
  FaTrash,
  FaLock,
  FaCheckCircle,
  FaExclamationTriangle,
  FaFilePdf,
  FaTruck,
} from 'react-icons/fa'
import dayjs from 'dayjs'

import { DropDownList, DropDownListFilterChangeEvent } from '@progress/kendo-react-dropdowns'
import { Label } from '@progress/kendo-react-labels'
import { Checkbox, Input, NumericTextBox, TextArea } from '@progress/kendo-react-inputs'
import { DatePicker } from '@progress/kendo-react-dateinputs'
import { filterBy } from '@progress/kendo-data-query'

import { FormNumericTextBox, FormTextArea } from '../FormComponents'
import { useToast } from '../../../contexts/ToastContext'
import { getEmployeeOptions } from '../../../data/employeeData'
import {
  dropDown,
  postPurchaseOrderItem,
  purchaseOrder,
  purchaseOrderItem,
} from '../../../data/interfaces'
import {
  addPurchaseOrderItem,
  createPurchaseOrder,
  deletePurchaseOrder,
  deletePurchaseOrderItem,
  getPurchaseOrder,
  getPurchaseOrderJobDefaults,
  openPurchaseOrderPdf,
  publishPurchaseOrder,
  setPurchaseOrderDelivered,
  setPurchaseOrderItemDelivered,
  savePurchaseOrder,
  updatePurchaseOrderItem,
} from '../../../data/purchaseOrderData'
import { getJobsForDropdown } from '../../../data/jobData'
import { getXeroSuppliersDropdown } from '../../../data/xeroData'
import {
  getAddressesForClientDropdown,
} from '../../../data/accountsData'

interface PurchaseOrderFormProps {
  /** Set to edit an existing order; omit to raise a new one. */
  purchaseOrderId?: number | null
  /**
   * Fixes the order to a job. Set when raising from within a job, so the user
   * is never asked for the job again.
   */
  jobId?: number | null
  lockEdit?: boolean
  /** Fired whenever the order is created, amended, published or deleted. */
  onSaved?: (order: purchaseOrder | null) => void
  onClose?: () => void
}

export const formatMoney = (value: number | null | undefined) =>
  typeof value === 'number'
    ? `£${value.toLocaleString('en-GB', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`
    : '£0.00'

/** Whole quantities read as "4", part quantities keep their decimals. */
export const formatQuantity = (value: number | null | undefined) =>
  typeof value !== 'number'
    ? '1'
    : Number.isInteger(value)
    ? String(value)
    : String(parseFloat(value.toFixed(2)))

// A blank line item, so "add" and "edit" can share one dialog.
const emptyLine = {
  purchaseOrderItemId: 0,
  description: '',
  quantity: 1 as number | null,
  itemValue: null as number | null,
  vatPercent: 20,
}

const PurchaseOrderForm = (props: PurchaseOrderFormProps) => {
  const { showToast } = useToast()

  const lockEdit = props.lockEdit ?? false

  const [order, setOrder] = useState<purchaseOrder | null>(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  // Header selections
  const [jobData, setJobData] = useState<dropDown[]>([])
  const [selectedJob, setSelectedJob] = useState<dropDown | null>(null)
  const [supplierData, setSupplierData] = useState<dropDown[]>([])
  const [selectedSupplier, setSelectedSupplier] = useState<dropDown | null>(null)
  const [siteData, setSiteData] = useState<dropDown[]>([])
  const [selectedSite, setSelectedSite] = useState<dropDown | null>(null)
  const [clientId, setClientId] = useState<number>(0)
  const [clientName, setClientName] = useState<string>('')
  const [supplierAddress, setSupplierAddress] = useState<string>('')
  const [supplierContact, setSupplierContact] = useState<string>('')
  const [supplierQuoteNumber, setSupplierQuoteNumber] = useState<string>('')
  const [supplierQuoteDate, setSupplierQuoteDate] = useState<Date | null>(null)
  const [deliverToHeadOffice, setDeliverToHeadOffice] = useState<boolean>(false)
  // The site contact is now chosen from the employees rather than inferred
  // from the job, so it holds a person rather than a job contact.
  const [employeeData, setEmployeeData] = useState<dropDown[]>([])
  const [selectedSiteContact, setSelectedSiteContact] = useState<dropDown | null>(null)

  // Line items. Before the order exists these are held here and posted with the
  // create call; afterwards each edit goes straight to the API.
  const [pendingItems, setPendingItems] = useState<postPurchaseOrderItem[]>([])
  const [lineDialogVisible, setLineDialogVisible] = useState(false)
  const [lineBeingEdited, setLineBeingEdited] = useState<typeof emptyLine | null>(null)
  /** Index into pendingItems while the order is unsaved; -1 for a new line. */
  const [pendingIndex, setPendingIndex] = useState(-1)
  const [formKey, setFormKey] = useState(0)

  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false)
  const [publishDialogVisible, setPublishDialogVisible] = useState(false)
  const [notDeliveredDialogVisible, setNotDeliveredDialogVisible] = useState(false)

  // The line whose delivery is being recorded, and how many have arrived.
  const [deliveryLine, setDeliveryLine] = useState<purchaseOrderItem | null>(null)
  const [deliveryQuantity, setDeliveryQuantity] = useState<number | null>(null)
  const [generatingPdf, setGeneratingPdf] = useState(false)

  // Once the allocated piece has gone to Xero the order is a matter of record.
  const readOnly = lockEdit || (order?.allocationLocked ?? false)

  //#region Loading

  const loadOrder = useCallback(async (id: number) => {
    setLoading(true)
    try {
      const loaded = await getPurchaseOrder(id)
      setOrder(loaded)
      setClientId(loaded.clientId)
      setClientName(loaded.clientName || '')
      setSelectedJob({ value: loaded.jobId, text: loaded.jobRef || `Job ${loaded.jobId}` })
      setSelectedSupplier(
        loaded.supplierId
          ? { value: loaded.supplierId, text: loaded.supplierName || '' }
          : null
      )
      setSelectedSite(
        loaded.siteId ? { value: loaded.siteId, text: loaded.siteAddress || '' } : null
      )
      setSupplierAddress(loaded.supplierAddress || '')
      setSupplierContact(loaded.supplierContact || '')
      setSupplierQuoteNumber(loaded.supplierQuoteNumber || '')
      setSupplierQuoteDate(
        loaded.supplierQuoteDate ? new Date(loaded.supplierQuoteDate) : null
      )
      setDeliverToHeadOffice(Boolean(loaded.deliverToHeadOffice))
      setSelectedSiteContact(
        loaded.siteContactEmployeeId
          ? {
              value: loaded.siteContactEmployeeId,
              text: loaded.siteContactName || '',
            }
          : null
      )
    } catch (error) {
      console.error('Error loading purchase order:', error)
      showToast('Failed to load the purchase order', 'error')
    } finally {
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /** Pulls client, site and contact across from the chosen job. */
  const applyJobDefaults = useCallback(async (jobId: number) => {
    try {
      const defaults = await getPurchaseOrderJobDefaults(jobId)
      setClientId(defaults.clientId)
      setClientName(defaults.clientName || '')
      setSelectedJob({ value: defaults.jobId, text: defaults.jobRef })
      setSelectedSite(
        defaults.siteId ? { value: defaults.siteId, text: defaults.siteAddress || '' } : null
      )
    } catch (error) {
      console.error('Error loading job details:', error)
      showToast('Failed to load the job details', 'error')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const fetchSuppliers = async () => {
      try {
        setSupplierData(await getXeroSuppliersDropdown())
      } catch (error) {
        console.error('Error loading suppliers:', error)
        showToast('Failed to load suppliers', 'error')
      }
    }

    fetchSuppliers()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Everyone active, for the site contact picker. An empty search term is what
  // the dropdown helper treats as "no filter", so this is the whole list.
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        setEmployeeData(await getEmployeeOptions())
      } catch (error) {
        // The order still saves without one, so this is not worth a toast.
        console.error('Error loading employees:', error)
      }
    }

    fetchEmployees()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // The job picker is only needed when the order isn't already pinned to a job.
  useEffect(() => {
    if (props.jobId || props.purchaseOrderId) {
      return
    }

    const fetchJobs = async () => {
      try {
        setJobData(await getJobsForDropdown())
      } catch (error) {
        console.error('Error loading jobs:', error)
        showToast('Failed to load jobs', 'error')
      }
    }

    fetchJobs()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.jobId, props.purchaseOrderId])

  useEffect(() => {
    if (props.purchaseOrderId) {
      loadOrder(props.purchaseOrderId)
    } else {
      setOrder(null)
      setPendingItems([])
      if (props.jobId) {
        applyJobDefaults(props.jobId)
      }
    }
  }, [props.purchaseOrderId, props.jobId, loadOrder, applyJobDefaults])

  // Sites follow whichever client the job resolved to. Contacts used to be
  // loaded here too, for a picker that has since been replaced by a free text
  // supplier contact.
  useEffect(() => {
    if (!clientId) {
      setSiteData([])
      return
    }

    const fetchForClient = async () => {
      try {
        setSiteData(await getAddressesForClientDropdown(clientId))
      } catch (error) {
        console.error('Error loading client sites:', error)
      }
    }

    fetchForClient()
  }, [clientId])

  //#endregion

  //#region Line items

  const liveItems: purchaseOrderItem[] = useMemo(() => {
    if (order) {
      return order.items
    }

    // Give unsaved lines the same shape the grid renders, with a negative id so
    // React keys stay unique before the server has assigned real ones.
    return pendingItems.map((item, index) => ({
      purchaseOrderItemId: -(index + 1),
      purchaseOrderId: 0,
      description: item.description,
      quantity: item.quantity,
      itemValue: item.itemValue,
      netValue: round2(item.quantity * item.itemValue),
      vatPercent: item.vatPercent,
      vatAmount:
        item.vatAmount ?? round2((item.quantity * item.itemValue * item.vatPercent) / 100),
      lineTotal:
        round2(item.quantity * item.itemValue) +
        (item.vatAmount ?? round2((item.quantity * item.itemValue * item.vatPercent) / 100)),
      // Nothing can have arrived on an order that doesn't exist yet.
      quantityDelivered: 0,
      quantityOutstanding: item.quantity,
      isDelivered: false,
      isPartDelivered: false,
      deliveredDate: null,
      deliveredByName: null,
      createdDate: null,
      createdByName: null,
      modifiedDate: null,
      modifiedByName: null,
    }))
  }, [order, pendingItems])

  const totals = useMemo(
    () => ({
      net: liveItems.reduce((sum, i) => sum + i.netValue, 0),
      vat: liveItems.reduce((sum, i) => sum + i.vatAmount, 0),
      gross: liveItems.reduce((sum, i) => sum + i.lineTotal, 0),
    }),
    [liveItems]
  )

  const handleAddLine = () => {
    setLineBeingEdited({ ...emptyLine })
    setPendingIndex(-1)
    setFormKey((prev) => prev + 1)
    setLineDialogVisible(true)
  }

  const handleEditLine = (item: purchaseOrderItem) => {
    if (readOnly) return

    setLineBeingEdited({
      purchaseOrderItemId: item.purchaseOrderItemId,
      description: item.description || '',
      quantity: item.quantity,
      itemValue: item.itemValue,
      vatPercent: item.vatPercent,
    })
    // Unsaved lines carry a negative id that maps back to their array position.
    setPendingIndex(order ? -1 : -item.purchaseOrderItemId - 1)
    setFormKey((prev) => prev + 1)
    setLineDialogVisible(true)
  }

  const handleLineSubmit = async (values: { [name: string]: any }) => {
    const payload: postPurchaseOrderItem = {
      description: values.description?.trim() ? values.description.trim() : null,
      quantity: Number(values.quantity ?? 1),
      itemValue: Number(values.itemValue ?? 0),
      vatPercent: Number(values.vatPercent ?? 0),
    }

    // Unsaved order: keep the line locally until the order is created.
    if (!order) {
      setPendingItems((prev) =>
        pendingIndex >= 0
          ? prev.map((item, index) => (index === pendingIndex ? payload : item))
          : [...prev, payload]
      )
      setLineDialogVisible(false)
      return
    }

    setSaving(true)
    try {
      if (lineBeingEdited && lineBeingEdited.purchaseOrderItemId > 0) {
        await updatePurchaseOrderItem(lineBeingEdited.purchaseOrderItemId, payload)
        showToast('Line updated', 'success')
      } else {
        await addPurchaseOrderItem(order.purchaseOrderId, payload)
        showToast('Line added', 'success')
      }
      await refresh(order.purchaseOrderId)
      setLineDialogVisible(false)
    } catch (error) {
      console.error('Error saving purchase order line:', error)
      showToast(errorMessage(error, 'Failed to save the line'), 'error')
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteLine = async (item: purchaseOrderItem) => {
    if (readOnly) return

    if (!order) {
      const index = -item.purchaseOrderItemId - 1
      setPendingItems((prev) => prev.filter((_, i) => i !== index))
      return
    }

    setSaving(true)
    try {
      await deletePurchaseOrderItem(item.purchaseOrderItemId)
      await refresh(order.purchaseOrderId)
      showToast('Line removed', 'success')
    } catch (error) {
      console.error('Error deleting purchase order line:', error)
      showToast(errorMessage(error, 'Failed to remove the line'), 'error')
    } finally {
      setSaving(false)
    }
  }

  //#endregion

  //#region Saving

  const refresh = async (id: number) => {
    const reloaded = await getPurchaseOrder(id)
    setOrder(reloaded)
    props.onSaved?.(reloaded)
    return reloaded
  }

  const headerPayload = () => ({
    clientId: clientId || null,
    siteId: selectedSite?.value || null,
    supplierId: Number(selectedSupplier?.value ?? 0),
    supplierAddress: supplierAddress.trim() || null,
    supplierContact: supplierContact.trim() || null,
    supplierQuoteNumber: supplierQuoteNumber.trim() || null,
    // Date only, built from the local parts rather than toISOString(), which
    // would shift a UK date back a day over BST.
    supplierQuoteDate: supplierQuoteDate
      ? `${supplierQuoteDate.getFullYear()}-${String(supplierQuoteDate.getMonth() + 1).padStart(2, '0')}-${String(supplierQuoteDate.getDate()).padStart(2, '0')}`
      : null,
    deliverToHeadOffice,
    siteContactEmployeeId: selectedSiteContact?.value || null,
  })

  const validateHeader = () => {
    const jobId = props.jobId || selectedJob?.value

    if (!jobId) {
      showToast('Choose the job this purchase order is for', 'error')
      return null
    }
    if (!selectedSupplier?.value) {
      showToast('Choose a supplier', 'error')
      return null
    }

    return Number(jobId)
  }

  /** Saves without publishing, so a half finished order can be parked. */
  const handleSaveDraft = async () => {
    const jobId = validateHeader()
    if (jobId === null) return

    setSaving(true)
    try {
      if (order) {
        await savePurchaseOrder({
          purchaseOrderId: order.purchaseOrderId,
          ...headerPayload(),
        })
        await refresh(order.purchaseOrderId)
        showToast('Purchase order saved', 'success')
      } else {
        const created = await createPurchaseOrder({
          jobId,
          ...headerPayload(),
          isDraft: true,
          items: pendingItems,
        })
        setOrder(created)
        setPendingItems([])
        props.onSaved?.(created)
        showToast('Draft purchase order saved', 'success')
      }
    } catch (error) {
      console.error('Error saving purchase order:', error)
      showToast(errorMessage(error, 'Failed to save the purchase order'), 'error')
    } finally {
      setSaving(false)
    }
  }

  /**
   * Commits the order. A new order is created and published in one step; an
   * existing draft is saved first so any header edits go up with it.
   */
  const handleGeneratePdf = async () => {
    if (!order) return

    try {
      setGeneratingPdf(true)
      await openPurchaseOrderPdf(order.purchaseOrderId)
    } catch (error: any) {
      console.error('Error generating purchase order PDF:', error)
      showToast(error?.message || 'Failed to generate the purchase order PDF', 'error')
    } finally {
      setGeneratingPdf(false)
    }
  }

  /** The whole order: every line in full, or - with false - every line cleared. */
  const handleSetDelivered = async (delivered: boolean) => {
    if (!order) return

    setSaving(true)
    try {
      const updated = await setPurchaseOrderDelivered(order.purchaseOrderId, delivered)
      setOrder(updated)
      props.onSaved?.(updated)
      setNotDeliveredDialogVisible(false)
      showToast(
        delivered ? 'Purchase order marked as delivered' : 'Delivery marks removed',
        'success'
      )
    } catch (error) {
      console.error('Error updating delivery status:', error)
      showToast(errorMessage(error, 'Failed to update the delivery status'), 'error')
    } finally {
      setSaving(false)
    }
  }

  const openDeliveryDialog = (item: purchaseOrderItem) => {
    setDeliveryLine(item)
    // Marking a line delivered usually means all of it - but a line already
    // part delivered opens on what was recorded, ready to be corrected.
    setDeliveryQuantity(item.quantityDelivered > 0 ? item.quantityDelivered : item.quantity)
  }

  /** One line: how many of it have arrived in total so far. */
  const handleSetLineDelivered = async () => {
    if (!order || !deliveryLine || deliveryQuantity === null) return

    setSaving(true)
    try {
      const updated = await setPurchaseOrderItemDelivered(
        deliveryLine.purchaseOrderItemId,
        deliveryQuantity
      )
      setOrder(updated)
      props.onSaved?.(updated)
      setDeliveryLine(null)
      showToast(
        deliveryQuantity === 0
          ? 'Line marked as not delivered'
          : deliveryQuantity >= deliveryLine.quantity
          ? updated.isDelivered
            ? 'Line delivered - the whole order has now arrived'
            : 'Line marked as delivered'
          : `Line part delivered: ${formatQuantity(deliveryQuantity)} of ${formatQuantity(deliveryLine.quantity)}`,
        'success'
      )
    } catch (error) {
      console.error('Error updating line delivery:', error)
      showToast(errorMessage(error, 'Failed to update the delivery'), 'error')
    } finally {
      setSaving(false)
    }
  }

  const handlePublish = async () => {
    const jobId = validateHeader()
    if (jobId === null) return

    if (liveItems.length === 0) {
      showToast('Add at least one line before publishing', 'error')
      return
    }

    setSaving(true)
    try {
      if (order) {
        await savePurchaseOrder({
          purchaseOrderId: order.purchaseOrderId,
          ...headerPayload(),
        })
        const published = await publishPurchaseOrder(order.purchaseOrderId)
        setOrder(published)
        props.onSaved?.(published)
        showToast(`Purchase order ${published.orderNo} published`, 'success')
      } else {
        const created = await createPurchaseOrder({
          jobId,
          ...headerPayload(),
          isDraft: false,
          items: pendingItems,
        })
        setOrder(created)
        setPendingItems([])
        props.onSaved?.(created)
        showToast(`Purchase order ${created.orderNo} published`, 'success')
      }
      setPublishDialogVisible(false)
    } catch (error) {
      console.error('Error publishing purchase order:', error)
      showToast(errorMessage(error, 'Failed to publish the purchase order'), 'error')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!order) return

    setSaving(true)
    try {
      await deletePurchaseOrder(order.purchaseOrderId)
      showToast('Purchase order deleted', 'success')
      setDeleteDialogVisible(false)
      props.onSaved?.(null)
      props.onClose?.()
    } catch (error) {
      console.error('Error deleting purchase order:', error)
      showToast(errorMessage(error, 'Failed to delete the purchase order'), 'error')
    } finally {
      setSaving(false)
    }
  }

  //#endregion

  //#region Cells

  const quantityCell = (cellProps: any) => (
    <td>{formatQuantity(cellProps.dataItem.quantity)}</td>
  )
  const unitCell = (cellProps: any) => <td>{formatMoney(cellProps.dataItem.itemValue)}</td>
  const valueCell = (cellProps: any) => <td>{formatMoney(cellProps.dataItem.netValue)}</td>
  const vatCell = (cellProps: any) => (
    <td>
      {formatMoney(cellProps.dataItem.vatAmount)}
      <span style={{ color: '#8a8a8a' }}> ({cellProps.dataItem.vatPercent}%)</span>
    </td>
  )
  const totalCell = (cellProps: any) => (
    <td style={{ fontWeight: 600 }}>{formatMoney(cellProps.dataItem.lineTotal)}</td>
  )

  const deliveredCell = (cellProps: any) => {
    const item: purchaseOrderItem = cellProps.dataItem
    const counts = `${formatQuantity(item.quantityDelivered)} of ${formatQuantity(item.quantity)}`
    const when = item.deliveredDate
      ? `Updated ${dayjs(item.deliveredDate).format('DD/MM/YYYY')}${item.deliveredByName ? ` by ${item.deliveredByName}` : ''}`
      : undefined

    return (
      <td title={when}>
        {item.isDelivered ? (
          <span style={{ color: 'rgb(94 142 23)', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <FaCheckCircle /> {counts}
          </span>
        ) : item.isPartDelivered ? (
          <span style={{ color: '#a37800', fontWeight: 600 }}>{counts}</span>
        ) : (
          <span style={{ color: '#8a8a8a' }}>{counts}</span>
        )}
      </td>
    )
  }

  // Deliveries can only be recorded against a published order.
  const canRecordDelivery = !!order && !order.isDraft && !readOnly

  const lineActionCell = (cellProps: any) => (
    <td style={{ textAlign: 'center' }}>
      {!readOnly && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: '10px' }}>
          {canRecordDelivery && (
            <FaTruck
              style={{ cursor: 'pointer', color: 'rgb(94 142 23)', fontSize: '16px' }}
              title='Record delivery'
              onClick={() => openDeliveryDialog(cellProps.dataItem)}
            />
          )}
          <FaEdit
            style={{ cursor: 'pointer', color: '#1e2a4e', fontSize: '16px' }}
            title='Edit line'
            onClick={() => handleEditLine(cellProps.dataItem)}
          />
          <FaTrash
            style={{ cursor: 'pointer', color: '#e9530d', fontSize: '16px' }}
            title='Remove line'
            onClick={() => handleDeleteLine(cellProps.dataItem)}
          />
        </div>
      )}
    </td>
  )

  //#endregion

  if (loading) {
    return <div style={{ padding: '1rem' }}>Loading purchase order...</div>
  }

  return (
    <div className='form__wrapper__notabs' style={{ padding: '1rem' }}>
      {/* Reference and status */}
      <div className='form__details__subcontainer' style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: '10pt', color: 'rgb(36,32,56)' }}>Order No:</div>
            <div style={{ fontWeight: 600, fontSize: '12pt' }}>
              {order?.orderNo || 'Generated when published'}
            </div>
          </div>
          {order && (
            <div>
              <div style={{ fontSize: '10pt', color: 'rgb(36,32,56)' }}>Status:</div>
              <div style={{ fontWeight: 600 }}>
                {order.isDraft ? (
                  <span style={{ color: '#a37800' }}>Draft</span>
                ) : order.isDelivered ? (
                  <span style={{ color: 'rgb(94 142 23)' }}>Delivered</span>
                ) : order.isPartDelivered ? (
                  <span style={{ color: '#a37800' }}>Part Delivered</span>
                ) : (
                  <span style={{ color: 'rgb(94 142 23)' }}>Published</span>
                )}
              </div>
            </div>
          )}
          {order?.publishedDate && (
            <div>
              <div style={{ fontSize: '10pt', color: 'rgb(36,32,56)' }}>Published:</div>
              <div>
                {dayjs(order.publishedDate).format('DD/MM/YYYY')}
                {order.publishedByName ? ` by ${order.publishedByName}` : ''}
              </div>
            </div>
          )}
          {order?.deliveredDate && (
            <div>
              <div style={{ fontSize: '10pt', color: 'rgb(36,32,56)' }}>Delivered:</div>
              <div>
                {dayjs(order.deliveredDate).format('DD/MM/YYYY')}
                {order.deliveredByName ? ` by ${order.deliveredByName}` : ''}
              </div>
            </div>
          )}
        </div>

        {order?.piecesId && (
          <div
            style={{
              marginTop: '0.75rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              color: order.allocationLocked ? '#5c4600' : 'rgb(94 142 23)',
            }}
          >
            {order.allocationLocked ? <FaLock /> : <FaCheckCircle />}
            <span>
              Allocated to Pieces invoice {order.allocatedPiecesInvoiceId || order.piecesId}
              {order.allocationLocked
                ? ' - sent to Xero, so this purchase order can no longer be changed.'
                : ''}
            </span>
          </div>
        )}
      </div>

      {/* Header. Driven by local state rather than a Kendo Form, because the
          client, site and contact all have to follow whichever job is chosen -
          and a Field always takes its value from the form, not from a prop. */}
      <div className='form__details__subcontainer'>
        {props.jobId || order ? (
          <div style={{ marginBottom: '0.75rem' }}>
            <div style={fieldLabelStyle}>Job:</div>
            <div style={{ fontWeight: 600 }}>{selectedJob?.text || `Job ${props.jobId}`}</div>
          </div>
        ) : (
          <LabelledDropDown
            label='Job:'
            data={jobData}
            value={selectedJob}
            disabled={readOnly}
            placeholder='Select a job'
            onChange={(picked) => {
              setSelectedJob(picked)
              if (picked?.value) {
                applyJobDefaults(Number(picked.value))
              }
            }}
          />
        )}

        <div style={{ marginBottom: '0.75rem' }}>
          <div style={fieldLabelStyle}>Client:</div>
          <div style={{ fontWeight: 600 }}>{clientName || '-'}</div>
        </div>

        {/* Ordered supplier first, then the site: the supplier block is what
            the order is placed with, and the site is where it goes. */}
        <LabelledDropDown
          label='Supplier:'
          data={supplierData}
          value={selectedSupplier}
          disabled={readOnly}
          placeholder='Select a supplier'
          onChange={setSelectedSupplier}
        />

        <div style={{ marginBottom: '0.75rem' }}>
          <div style={fieldLabelStyle}>Supplier Address:</div>
          <TextArea
            value={supplierAddress}
            disabled={readOnly}
            rows={3}
            placeholder="The supplier's address, as it should appear on the order"
            onChange={(e) => setSupplierAddress(String(e.value ?? ''))}
            style={{ width: '100%' }}
          />
        </div>

        <div style={{ marginBottom: '0.75rem' }}>
          <div style={fieldLabelStyle}>Supplier Contact:</div>
          <Input
            value={supplierContact}
            disabled={readOnly}
            maxLength={200}
            placeholder='Who to ask for at the supplier'
            onChange={(e) => setSupplierContact(String(e.value ?? ''))}
            style={{ width: '100%' }}
          />
        </div>


        <div style={{ marginBottom: '0.75rem' }}>
          <div style={fieldLabelStyle}>Supplier Quote Number:</div>
          <Input
            value={supplierQuoteNumber}
            disabled={readOnly}
            maxLength={100}
            placeholder="The supplier's own quote reference"
            onChange={(e) => setSupplierQuoteNumber(String(e.value ?? ''))}
            style={{ width: '100%' }}
          />
        </div>

        <div style={{ marginBottom: '0.75rem' }}>
          <div style={fieldLabelStyle}>Supplier Quote Date:</div>
          <DatePicker
            value={supplierQuoteDate}
            disabled={readOnly}
            format='dd/MM/yyyy'
            onChange={(e) => setSupplierQuoteDate(e.value)}
            width='100%'
          />
        </div>

        <div style={{ marginBottom: '0.75rem' }}>
          <div
            style={{
              ...fieldLabelStyle,
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              flexWrap: 'wrap',
            }}
          >
            <span>Site:</span>
            <Checkbox
              value={deliverToHeadOffice}
              disabled={readOnly}
              label='Deliver to Bamfords head office'
              onChange={(e) => setDeliverToHeadOffice(Boolean(e.value))}
            />
          </div>
          <DropDownList
            data={siteData}
            textField='text'
            dataItemKey='value'
            value={selectedSite}
            // Locked once delivery goes to head office: the site no longer
            // decides where anything is sent, so leaving it editable would
            // suggest it still did. Whatever was chosen stays on the order.
            disabled={readOnly || deliverToHeadOffice}
            onChange={(e) => setSelectedSite(e.value)}
            style={{ width: '100%', marginTop: '0.5rem' }}
          />
          {deliverToHeadOffice && (
            <div style={{ fontSize: '11px', color: '#5a6472', marginTop: '0.3rem' }}>
              The order will show Bamfords head office as the delivery address.
            </div>
          )}
        </div>

        <LabelledDropDown
          label='Site Contact:'
          data={employeeData}
          value={selectedSiteContact}
          disabled={readOnly}
          placeholder='Select a site contact'
          onChange={setSelectedSiteContact}
        />
      </div>

      {/* Line items */}
      <div style={{ marginTop: '1.5rem' }}>
        <Grid data={liveItems} style={{ maxHeight: '320px' }}>
          <GridToolbar>
            <span style={{ color: '#666' }}>
              Lines: {liveItems.length} | Net: {formatMoney(totals.net)} | VAT:{' '}
              {formatMoney(totals.vat)} | <strong>Total: {formatMoney(totals.gross)}</strong>
            </span>
            {!readOnly && (
              <Button type='button' themeColor='primary' onClick={handleAddLine}>
                Add Line
              </Button>
            )}
          </GridToolbar>
          <GridColumn field='description' title='Description' />
          <GridColumn field='quantity' title='Qty' width='90px' cell={quantityCell} />
          <GridColumn field='itemValue' title='Unit Price' width='130px' cell={unitCell} />
          <GridColumn field='netValue' title='Net' width='130px' cell={valueCell} />
          <GridColumn field='vatAmount' title='VAT' width='160px' cell={vatCell} />
          <GridColumn field='lineTotal' title='Total' width='130px' cell={totalCell} />
          {order && !order.isDraft && (
            <GridColumn field='quantityDelivered' title='Delivered' width='120px' cell={deliveredCell} />
          )}
          <GridColumn title='Actions' width='120px' cell={lineActionCell} />
        </Grid>

        {liveItems.length === 0 && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              marginTop: '0.75rem',
              color: '#8a6d00',
            }}
          >
            <FaExclamationTriangle />
            <span>Add at least one line before publishing this purchase order.</span>
          </div>
        )}
      </div>

      {/* Actions */}
      {(!readOnly || (order != null && !order.isDraft)) && (
        <div
          style={{
            display: 'flex',
            gap: '0.75rem',
            marginTop: '1.5rem',
            flexWrap: 'wrap',
          }}
        >
          {/* A draft carries no order number and has not been placed with
              anyone, so there is nothing to send yet. Publishing stamps the
              number the document is built around. Offered even when the order
              is locked - a sent-to-Xero order can still be reprinted. */}
          {order && !order.isDraft && (
            <Button
              type='button'
              onClick={handleGeneratePdf}
              disabled={generatingPdf}
              title='Generate the purchase order PDF and open it in a new window'
            >
              <FaFilePdf style={{ marginRight: '0.4rem' }} />
              {generatingPdf ? 'Generating...' : 'Generate PDF'}
            </Button>
          )}
          {!readOnly && (!order || order.isDraft) && (
            <Button type='button' onClick={handleSaveDraft} disabled={saving}>
              {saving ? 'Saving...' : 'Save Draft'}
            </Button>
          )}
          {!readOnly && order && !order.isDraft && (
            <Button type='button' onClick={handleSaveDraft} disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          )}
          {!readOnly && (!order || order.isDraft) && (
            <Button
              type='button'
              themeColor='primary'
              onClick={() => setPublishDialogVisible(true)}
              disabled={saving || liveItems.length === 0}
            >
              Publish
            </Button>
          )}
          {/* The whole order at once. Single lines are marked from the truck
              icon on each line. */}
          {order && canRecordDelivery && !order.isDelivered && (
            <Button
              type='button'
              themeColor='success'
              onClick={() => handleSetDelivered(true)}
              disabled={saving}
              title='Mark every line as fully delivered'
            >
              {order.isPartDelivered ? 'Mark Rest as Delivered' : 'Mark as Delivered'}
            </Button>
          )}
          {order && canRecordDelivery && (order.isDelivered || order.isPartDelivered) && (
            <Button
              type='button'
              onClick={() => setNotDeliveredDialogVisible(true)}
              disabled={saving}
              title='Clear what has been recorded as delivered on every line'
            >
              Mark as Not Delivered
            </Button>
          )}
          {!readOnly && order && (
            <Button
              type='button'
              themeColor='error'
              onClick={() => setDeleteDialogVisible(true)}
              disabled={saving}
            >
              Delete
            </Button>
          )}
        </div>
      )}

      {/* Line dialog */}
      {lineDialogVisible && (
        <Dialog
          title={
            lineBeingEdited && lineBeingEdited.purchaseOrderItemId !== 0
              ? 'Edit Line'
              : 'Add Line'
          }
          onClose={() => setLineDialogVisible(false)}
          appendTo={document.body}
          width='520px'
        >
          <Form
            key={formKey}
            initialValues={{
              description: lineBeingEdited?.description || '',
              quantity: lineBeingEdited?.quantity ?? 1,
              itemValue: lineBeingEdited?.itemValue ?? null,
              vatPercent: lineBeingEdited?.vatPercent ?? 20,
            }}
            onSubmit={handleLineSubmit}
            render={(formRenderProps) => (
              <FormElement style={{ maxWidth: 520 }}>
                <div className='form__details__subcontainer'>
                  <Field
                    name='description'
                    component={FormTextArea}
                    label='Description:'
                    rows={3}
                  />
                  <Field
                    name='quantity'
                    component={FormNumericTextBox}
                    label='Quantity:'
                    format='n2'
                    min={0}
                    step={1}
                  />
                  <Field
                    name='itemValue'
                    component={FormNumericTextBox}
                    label='Unit Price (£):'
                    format='n2'
                    step={0.01}
                  />
                  <Field
                    name='vatPercent'
                    component={FormNumericTextBox}
                    label='VAT Rate (%):'
                    format='n2'
                    step={0.5}
                  />
                  <div style={{ fontSize: '9pt', color: '#8a8a8a', marginTop: '0.5rem' }}>
                    Net is quantity &times; unit price
                    {' — '}
                    {formatMoney(
                      round2(
                        Number(formRenderProps.valueGetter('quantity') ?? 0) *
                          Number(formRenderProps.valueGetter('itemValue') ?? 0)
                      )
                    )}
                    . The VAT amount is worked out from that net and the rate.
                  </div>
                </div>
                <DialogActionsBar>
                  <Button type='button' onClick={() => setLineDialogVisible(false)}>
                    Cancel
                  </Button>
                  <Button
                    type='submit'
                    themeColor='primary'
                    disabled={!formRenderProps.allowSubmit || saving}
                  >
                    {saving ? 'Saving...' : 'Save Line'}
                  </Button>
                </DialogActionsBar>
              </FormElement>
            )}
          />
        </Dialog>
      )}

      {/* Record a delivery against one line */}
      {deliveryLine && (
        <Dialog
          title='Record Delivery'
          onClose={() => setDeliveryLine(null)}
          appendTo={document.body}
          width='460px'
        >
          <div style={{ padding: '1rem' }}>
            <p style={{ marginTop: 0 }}>
              <strong>{deliveryLine.description || 'Line'}</strong>
            </p>
            <p>
              <strong>Ordered:</strong> {formatQuantity(deliveryLine.quantity)}
              {deliveryLine.quantityDelivered > 0 && (
                <>
                  <br />
                  <strong>Recorded so far:</strong> {formatQuantity(deliveryLine.quantityDelivered)}
                  {deliveryLine.deliveredDate &&
                    ` (${dayjs(deliveryLine.deliveredDate).format('DD/MM/YYYY')}${
                      deliveryLine.deliveredByName ? ` by ${deliveryLine.deliveredByName}` : ''
                    })`}
                </>
              )}
            </p>
            <div style={fieldLabelStyle}>Quantity delivered in total:</div>
            <NumericTextBox
              value={deliveryQuantity}
              onChange={(e) => setDeliveryQuantity(e.value)}
              format='n2'
              min={0}
              max={deliveryLine.quantity}
              step={1}
              width='100%'
            />
            <div style={{ fontSize: '9pt', color: '#8a8a8a', marginTop: '0.5rem' }}>
              {deliveryQuantity === null
                ? 'Enter how many have arrived.'
                : deliveryQuantity > deliveryLine.quantity
                ? `Only ${formatQuantity(deliveryLine.quantity)} were ordered.`
                : deliveryQuantity === 0
                ? 'The line will be marked as not delivered.'
                : deliveryQuantity < deliveryLine.quantity
                ? `Part delivered - ${formatQuantity(deliveryLine.quantity - deliveryQuantity)} still to come.`
                : 'The whole line has arrived.'}
            </div>
          </div>
          <DialogActionsBar>
            <Button type='button' onClick={() => setDeliveryLine(null)}>
              Cancel
            </Button>
            <Button
              type='button'
              themeColor='primary'
              onClick={handleSetLineDelivered}
              disabled={
                saving ||
                deliveryQuantity === null ||
                deliveryQuantity < 0 ||
                deliveryQuantity > deliveryLine.quantity
              }
            >
              {saving ? 'Saving...' : 'Confirm'}
            </Button>
          </DialogActionsBar>
        </Dialog>
      )}

      {/* Clear every line's deliveries */}
      {notDeliveredDialogVisible && (
        <Dialog
          title='Mark as Not Delivered'
          onClose={() => setNotDeliveredDialogVisible(false)}
          appendTo={document.body}
          width='420px'
        >
          <div style={{ padding: '1rem' }}>
            <p style={{ marginTop: 0 }}>
              This clears what has been recorded as delivered on every line of{' '}
              {order?.orderNo || 'this purchase order'}.
            </p>
            <p>To correct a single line, use the truck icon on that line instead.</p>
          </div>
          <DialogActionsBar>
            <Button type='button' onClick={() => setNotDeliveredDialogVisible(false)}>
              Cancel
            </Button>
            <Button type='button' themeColor='error' onClick={() => handleSetDelivered(false)} disabled={saving}>
              {saving ? 'Saving...' : 'Mark as Not Delivered'}
            </Button>
          </DialogActionsBar>
        </Dialog>
      )}

      {/* Publish confirmation */}
      {publishDialogVisible && (
        <Dialog
          title='Publish Purchase Order'
          onClose={() => setPublishDialogVisible(false)}
          appendTo={document.body}
          width='460px'
        >
          <div style={{ padding: '1rem' }}>
            <p>
              Publishing assigns this purchase order its number and makes it available to
              match against incoming Pieces invoices.
            </p>
            <p>
              <strong>Lines:</strong> {liveItems.length}
            </p>
            <p>
              <strong>Total:</strong> {formatMoney(totals.gross)}
            </p>
          </div>
          <DialogActionsBar>
            <Button type='button' onClick={() => setPublishDialogVisible(false)}>
              Cancel
            </Button>
            <Button
              type='button'
              themeColor='primary'
              onClick={handlePublish}
              disabled={saving}
            >
              {saving ? 'Publishing...' : 'Publish'}
            </Button>
          </DialogActionsBar>
        </Dialog>
      )}

      {/* Delete confirmation */}
      {deleteDialogVisible && (
        <Dialog
          title='Confirm Delete'
          onClose={() => setDeleteDialogVisible(false)}
          appendTo={document.body}
          width='420px'
        >
          <div style={{ padding: '1rem' }}>
            <p>Are you sure you want to delete this purchase order and all its lines?</p>
            <p>
              <strong>Order No:</strong> {order?.orderNo || 'Draft'}
            </p>
            <p>
              <strong>Supplier:</strong> {order?.supplierName || '—'}
            </p>
            <p>
              <strong>Total:</strong> {formatMoney(order?.grossValue)}
            </p>
          </div>
          <DialogActionsBar>
            <Button type='button' onClick={() => setDeleteDialogVisible(false)}>
              Cancel
            </Button>
            <Button
              type='button'
              themeColor='error'
              onClick={handleDelete}
              disabled={saving}
            >
              {saving ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActionsBar>
        </Dialog>
      )}
    </div>
  )
}

const fieldLabelStyle: React.CSSProperties = {
  fontSize: '10pt',
  color: 'rgb(36,32,56)',
}

interface LabelledDropDownProps {
  label: string
  data: dropDown[]
  value: dropDown | null
  disabled?: boolean
  placeholder: string
  onChange: (value: dropDown | null) => void
}

/**
 * A filterable dropdown that takes its value from a prop. Deliberately not a
 * Kendo Form Field: a Field always renders the form's own value, which would
 * ignore anything set programmatically - like the defaults pulled off a job.
 */
const LabelledDropDown = ({
  label,
  data,
  value,
  disabled,
  placeholder,
  onChange,
}: LabelledDropDownProps) => {
  const [filter, setFilter] = useState<string>('')

  const filtered = filter
    ? filterBy(data, { field: 'text', operator: 'contains', value: filter, ignoreCase: true })
    : data

  return (
    <div style={{ marginBottom: '0.75rem' }}>
      <Label style={fieldLabelStyle}>{label}</Label>
      <DropDownList
        data={filtered}
        textField='text'
        dataItemKey='value'
        value={value}
        disabled={disabled}
        filterable={true}
        onFilterChange={(event: DropDownListFilterChangeEvent) =>
          setFilter(event.filter.value)
        }
        onChange={(event) => onChange(event.value)}
        popupSettings={{ appendTo: document.body }}
        style={{ width: '100%' }}
        defaultItem={{ value: 0, text: placeholder }}
      />
    </div>
  )
}

const round2 = (value: number) => Math.round(value * 100) / 100

/** Surfaces the API's own message when it sent one, e.g. a locked allocation. */
const errorMessage = (error: any, fallback: string) =>
  error?.response?.data?.message || fallback

export default PurchaseOrderForm
